import {
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { GridComponent } from '@progress/kendo-angular-grid';

/**
 * Ensures Arrow Down/Up move focus to the immediate next/previous row and then
 * focuses the first focusable element in the cell (so the user can type without
 * pressing Enter). Use when the grid's or host's keyboard handling would otherwise
 * skip rows or leave the cell inactive.
 */
@Directive({
  selector: 'kendo-grid[upsGridGroupingSequentialRowNav]',
})
export class GridGroupingSequentialRowNavDirective
  implements OnInit, OnDestroy
{
  private unsubKeydown: (() => void) | null = null;

  constructor(
    private grid: GridComponent,
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.unsubKeydown = this.renderer.listen(
      this.el.nativeElement,
      'keydown',
      (e: KeyboardEvent) => this.onKeydown(e),
      { capture: true }
    );
  }

  ngOnDestroy(): void {
    this.unsubKeydown?.();
  }

  private onKeydown(e: KeyboardEvent): void {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
      return;
    }

    const activeCell = this.grid.activeCell;
    if (!activeCell) {
      return;
    }

    const rowIndex = activeCell.rowIndex;
    const colIndex = activeCell.colIndex;

    if (e.key === 'ArrowUp') {
      if (rowIndex <= 0) return;
      e.preventDefault();
      e.stopPropagation();
      this.grid.focusCell(rowIndex - 1, colIndex);
      this.enterCellAfterNav();
    } else {
      e.preventDefault();
      e.stopPropagation();
      this.grid.focusCell(rowIndex + 1, colIndex);
      this.enterCellAfterNav();
    }
  }

  /**
   * After moving focus to a new cell, simulate Enter so the grid focuses the
   * first focusable element inside the cell (input, combobox, etc.).
   */
  private enterCellAfterNav(): void {
    setTimeout(() => {
      this.el.nativeElement.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: 'Enter',
          code: 'Enter',
          keyCode: 13,
          bubbles: true,
          cancelable: true,
        })
      );
    }, 0);
  }
}
